import { KycIntegrationInfoDTO } from '@/models'
import api from './api'
import { AxiosResponse } from 'axios'

/**
 * Service for KYC (Know Your Customer) verification operations
 */
export class KycService {
  /**
   * Get KYC integration information for a client
   * Retrieves all necessary information for client applications to integrate Veriff KYC verification
   *
   * @param {number} clientId - The ID of the client
   * @param {boolean} autoInitiate - Whether to automatically initiate a new verification session if none exists (default: false)
   * @returns {Promise<KycIntegrationInfoDTO>} KYC integration information
   * @throws {Error} If the request fails or client is not found
   *
   * @example
   * ```typescript
   * // Get info without creating a new session
   * const kycInfo = await KycService.getClientKycIntegrationInfo(123);
   * 
   * // Get info and auto-create session if needed
   * const kycInfo = await KycService.getClientKycIntegrationInfo(123, true);
   * if (kycInfo.verificationRequired) {
   *   // Redirect to verification
   *   window.location.href = kycInfo.veriffSessionUrl;
   * }
   * ```
   */
  static async getClientKycIntegrationInfo(
    clientId: number,
    autoInitiate = false
  ): Promise<KycIntegrationInfoDTO> {
    try {
      if (!clientId || clientId <= 0) {
        throw new Error('Invalid client ID provided')
      }

      const response: AxiosResponse<KycIntegrationInfoDTO> = await api.get(
        `/api/verifications/kyc/client/${clientId}/integration-info`,
        {
          params: {
            autoInitiate
          }
        }
      )

      return response.data
    } catch (error: any) {
      // Enhanced error handling with specific messages
      if (error.response) {
        // Server responded with error status
        const status = error.response.status
        switch (status) {
          case 404:
            throw new Error(`Client with ID ${clientId} not found`)
          case 403:
            throw new Error('Access denied to KYC information')
          case 401:
            throw new Error('Authentication required')
          default:
            throw new Error(error.response.data?.message || `Failed to fetch KYC information (Status: ${status})`)
        }
      } else if (error.request) {
        // Request made but no response received
        throw new Error('Network error: Unable to reach the server')
      } else {
        // Something else happened
        throw error
      }
    }
  }

  /**
   * Check if client has valid KYC verification
   *
   * @param {number} clientId - The ID of the client
   * @returns {Promise<boolean>} True if client is KYC verified
   */
  static async isClientKycVerified(clientId: number): Promise<boolean> {
    try {
      const kycInfo = await this.getClientKycIntegrationInfo(clientId)
      return kycInfo.isKycCompliant === true
    } catch (error) {
      console.error('Error checking KYC verification status:', error)
      return false
    }
  }
}

export default KycService
